import { useCallback, useEffect, useRef } from 'react';
import { htmlToMarkdown } from '../lib/htmlToMarkdown.js';
import { renderMarkdown } from '../lib/markdown.js';

/**
 * One field that formats itself as you type.
 *
 * The two-pane version was correct and horrible: it made you read your own
 * note twice and gave half the width to a copy of it. Here the markdown you
 * type becomes the thing it describes — `## ` turns the line into a heading
 * and disappears, `**bold**` closes into bold — while what is *stored* is
 * still markdown, serialized back out of the DOM on every keystroke. Nothing
 * proprietary ends up in the database, and a note written in the app and a
 * note written in a text file are the same file.
 *
 * The field is uncontrolled on purpose: writing `value` back into it on every
 * render would put the caret back at the start of the line each time. It is
 * only re-rendered from the outside when the text differs from what this
 * component last produced — a different task opened, or a reset.
 */
export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  minHeight = '12rem',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
}) {
  const host = useRef<HTMLDivElement>(null);
  const mine = useRef<string>(value);

  useEffect(() => {
    const node = host.current;
    if (!node || value === mine.current) return;
    node.innerHTML = renderMarkdown(value);
    mine.current = value;
  }, [value]);

  useEffect(() => {
    // Paragraphs rather than bare divs, so headings and lists have something
    // to be turned back into.
    try {
      document.execCommand('defaultParagraphSeparator', false, 'p');
    } catch {
      /* not fatal: the browser's own default still works */
    }
  }, []);

  const publish = useCallback(() => {
    const node = host.current;
    if (!node) return;
    const markdown = htmlToMarkdown(node);
    mine.current = markdown;
    onChange(markdown);
  }, [onChange]);

  /** The text of the current line up to the caret, for the block rules. */
  const lineBeforeCaret = (): { text: string; node: Text; offset: number } | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return null;
    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return null;
    return { text: (node.textContent ?? '').slice(0, range.startOffset), node: node as Text, offset: range.startOffset };
  };

  /** Drop `count` characters immediately before the caret. */
  const eat = (node: Text, offset: number, count: number) => {
    node.deleteData(offset - count, count);
    const selection = window.getSelection();
    const range = document.createRange();
    range.setStart(node, offset - count);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  /**
   * Wrap the text just typed in a tag, and leave the caret outside it — so
   * what you type next is not also bold. A bare caret after an element still
   * belongs to it in most browsers, hence the empty text node to land in.
   */
  const wrap = (node: Text, offset: number, length: number, tag: 'strong' | 'em' | 'code', inner: string) => {
    const range = document.createRange();
    range.setStart(node, offset - length);
    range.setEnd(node, offset);
    range.deleteContents();

    const element = document.createElement(tag);
    element.textContent = inner;
    range.insertNode(element);

    const after = document.createTextNode('');
    element.parentNode?.insertBefore(after, element.nextSibling);
    const selection = window.getSelection();
    const caret = document.createRange();
    caret.setStart(after, 0);
    caret.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(caret);
  };

  const onInput = (event: React.FormEvent<HTMLDivElement>) => {
    const data = (event.nativeEvent as InputEvent).data;
    const line = lineBeforeCaret();

    if (line && data === ' ') {
      // Block rules fire on the space that follows the marker, so the marker
      // is never left sitting there as literal text.
      const heading = /^(#{1,4}) $/.exec(line.text);
      if (heading) {
        eat(line.node, line.offset, heading[0].length);
        document.execCommand('formatBlock', false, `h${heading[1]?.length ?? 1}`);
        publish();
        return;
      }
      if (/^[-*] $/.test(line.text)) {
        eat(line.node, line.offset, 2);
        document.execCommand('insertUnorderedList');
        publish();
        return;
      }
      if (/^\d+[.)] $/.test(line.text)) {
        eat(line.node, line.offset, line.text.length);
        document.execCommand('insertOrderedList');
        publish();
        return;
      }
      if (/^> $/.test(line.text)) {
        eat(line.node, line.offset, 2);
        document.execCommand('formatBlock', false, 'blockquote');
        publish();
        return;
      }
    }

    if (line && (data === '*' || data === '`')) {
      // Longest marker first: `**x**` must not be read as an italic `*x*`.
      const bold = /\*\*([^*]+)\*\*$/.exec(line.text);
      if (bold && bold[1]) {
        wrap(line.node, line.offset, bold[0].length, 'strong', bold[1]);
        publish();
        return;
      }
      const italic = /(^|[^*])\*([^*]+)\*$/.exec(line.text);
      if (italic && italic[2]) {
        wrap(line.node, line.offset, italic[2].length + 2, 'em', italic[2]);
        publish();
        return;
      }
      const code = /`([^`]+)`$/.exec(line.text);
      if (code && code[1]) {
        wrap(line.node, line.offset, code[0].length, 'code', code[1]);
        publish();
        return;
      }
    }

    publish();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!(event.metaKey || event.ctrlKey)) return;
    const key = event.key.toLowerCase();
    if (key === 'b' || key === 'i') {
      event.preventDefault();
      document.execCommand(key === 'b' ? 'bold' : 'italic');
      publish();
    }
  };

  return (
    <div
      ref={host}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label="Body"
      data-placeholder={placeholder}
      onInput={onInput}
      onKeyDown={onKeyDown}
      // A paste arrives as whatever the source was; taking its plain text and
      // letting the same rules read it keeps the document to the subset the
      // serializer can write back out.
      onPaste={(event) => {
        event.preventDefault();
        const text = event.clipboardData.getData('text/plain');
        document.execCommand('insertHTML', false, renderMarkdown(text));
        publish();
      }}
      className="markdown editable w-full overflow-y-auto rounded-[var(--radius-md)] px-3 py-2 text-sm outline-none"
      style={{ background: 'var(--surface-0)', minHeight, maxHeight: '32rem' }}
    />
  );
}
